import { Icon, MockBubble, type IconName } from '@nexa/ui';
import { useCopy } from '@/core/i18n';

/**
 * Pane 2 của mock app — `template.html:748`. Trợ lý AI đọc 3 nguồn rồi trả lời.
 *
 * Ba nguồn có icon và trạng thái khác nhau (dòng 758–760): hai nguồn đầu đã đọc xong (icon xanh,
 * dấu ✓), nguồn thứ ba đang đọc (icon brand, dấu ba chấm nhấp nháy). Đây là số ĐO, nên bảng dưới
 * đi theo CHỈ SỐ chứ không suy từ dữ liệu — copy chỉ có tên nguồn.
 */
const SOURCE_ICONS: readonly IconName[] = ['database', 'file-spreadsheet', 'folder'];
const READING_INDEX = 2;

export function AgentPane() {
  const agent = useCopy().hero.mock.agent;

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-[54px] items-center gap-2.5 border-b border-ink-100 bg-surface px-[18px]">
        <span className="grid size-[26px] flex-none place-items-center rounded-field bg-brand-50 text-nano font-bold text-brand-600">
          {agent.initials}
        </span>
        <span className="text-body font-bold text-ink-mock-900">{agent.name}</span>
        <span className="rounded-pill bg-brand-600 px-2 py-0.5 text-nano font-bold tracking-label text-white">
          AI
        </span>
        <span className="ml-auto hidden text-eyebrow text-ink-600 sm:block">{agent.tools}</span>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-[18px]">
        <MockBubble direction="out" className="max-w-[42ch] self-end">
          {agent.ask}
        </MockBubble>

        <div className="max-w-[52ch] rounded-[18px_18px_18px_6px] bg-surface px-4 py-3.5 shadow-hairline">
          <div className="mb-2.5 flex items-center gap-[7px] text-micro font-bold text-brand-600">
            <Icon name="sparkles" size={13} />
            <span>{agent.reading}</span>
          </div>

          <div className="mb-3 flex flex-col gap-1.5">
            {agent.sources.map((source, index) => {
              const reading = index === READING_INDEX;
              return (
                <div
                  key={source}
                  className="flex items-center gap-2 text-caption leading-[normal] text-ink-700"
                >
                  <Icon
                    name={SOURCE_ICONS[index] ?? 'folder'}
                    size={13}
                    className={reading ? 'text-brand-600' : 'text-success-fg'}
                  />
                  <span className="truncate">{source}</span>
                  {reading ? (
                    // Chu kỳ 1.4s của thiết kế, khác mặc định 2s — đổi bằng biến đã có sẵn trong
                    // `motion.css`, không thêm keyframes thứ hai.
                    <span className="nx-pulse ml-auto text-micro font-bold text-brand-600 [--nx-pulse-duration:1.4s]">
                      …
                    </span>
                  ) : (
                    /*
                     * `✓` (U+2713) RƠI VỀ FONT HỆ THỐNG — đo được: không face nào trong bundle
                     * thiết kế khai `unicode-range` phủ nó. Bản thiết kế gốc cũng vậy, nên đây
                     * là tái hiện trung thành chứ không phải bỏ sót. Cổng `check-fonts` không
                     * bắt được vì nó chỉ quét `packages/content`, không quét component.
                     */
                    <span className="ml-auto text-micro font-bold text-success-fg">✓</span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="text-body-sm leading-[normal] text-ink-mock-900">{agent.answer}</div>
        </div>

        <div className="flex flex-wrap gap-2">
          {agent.actions.map((action) => (
            <span
              key={action}
              className="rounded-pill border border-ink-300 px-3 py-1.5 text-caption leading-[normal] text-ink-800"
            >
              {action}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
